/**
 * Phase 135D — StreamX Stories (Weekly Editorial Layer)
 * ----------------------------------------------------
 * PUBLIC editorial surface at /streamx/stories. Reuses livex_replays under the
 * hood — zero new infrastructure. Curates the most-watched, most-shared,
 * and rising-academy replays from the last 7 days so visitors see the
 * StreamX network is alive and compounding.
 *
 * Design intent: ESPN/Hudl editorial energy. Cinematic. Premium sports media.
 * NOT dashboard cards. NOT admin analytics.
 */
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Eye, Share2, Sparkles, Trophy, Flame,
  TrendingUp, Film, Clock, Play, Calendar, Loader2, Radio,
} from 'lucide-react';
import { LOGO_URL, BrandText } from '@/lib/brand';
import LiveDot from '@/components/livex/LiveDot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Anim({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 18 }}
      animate={v ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const SPORT_GRADIENTS = {
  football: 'linear-gradient(135deg,#10b981,#059669)',
  cricket:  'linear-gradient(135deg,#3b82f6,#1d4ed8)',
  basketball: 'linear-gradient(135deg,#f97316,#dc2626)',
  badminton: 'linear-gradient(135deg,#a855f7,#7c3aed)',
  tennis: 'linear-gradient(135deg,#fbbf24,#f59e0b)',
  hockey: 'linear-gradient(135deg,#ef4444,#b91c1c)',
  volleyball: 'linear-gradient(135deg,#06b6d4,#0891b2)',
  default: 'linear-gradient(135deg,#FF3B30,#990000)',
};
const SPORT_EMOJI = {
  football: '⚽', cricket: '🏏', basketball: '🏀', badminton: '🏸',
  tennis: '🎾', hockey: '🏑', volleyball: '🏐',
};

function fmtDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}:${String(s).padStart(2, '0')}`;
  const h = Math.floor(m / 60);
  return `${h}:${String(m % 60).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function fmtRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const hours = (Date.now() - d.getTime()) / 3600000;
    if (hours < 1) return 'Moments ago';
    if (hours < 24) return `${Math.floor(hours)}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  } catch {
    return '';
  }
}

function fmtViews(n) {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function LivexStories() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'This Week on StreamX — Top Academy Moments | Out-Play';
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
    m.setAttribute('content', 'The most watched academy moments across the StreamX network. Weekly editorial picks of the best matches, highlights, and rising academies.');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/livex/stories/weekly`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load stories');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-white" style={{ background: '#0A0A0A' }} data-testid="livex-stories-loading">
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: '#FF3B30' }} />
      </div>
    );
  }

  const story = data?.story_of_the_week;
  const trending = data?.trending || [];
  const mostShared = data?.most_shared_moment;
  const rising = data?.rising_academy;
  const counts = data?.counts || {};
  const isEmpty = !story && trending.length === 0;

  return (
    <div className="min-h-screen text-white" style={{ background: '#0A0A0A' }} data-testid="livex-stories-page">
      <TopBar />
      <Hero counts={counts} />
      {error && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div data-testid="livex-stories-error" className="p-4 bg-rose-950 border-2 border-rose-700 rounded-xl text-rose-100 text-sm font-bold">
            {error}
          </div>
        </div>
      )}
      {isEmpty && !error ? (
        <EmptyState />
      ) : (
        <>
          {story && <StoryOfTheWeek story={story} />}
          {trending.length > 0 && <TrendingRow stories={trending} />}
          {mostShared && <MostSharedCallout story={mostShared} />}
          {rising && <RisingAcademy rising={rising} />}
        </>
      )}
      <BeFeaturedCTA />
      <Footer />
    </div>
  );
}

function TopBar() {
  return (
    <header
      className="sticky top-0 z-40 border-b"
      style={{
        background: 'rgba(0,0,0,0.65)',
        borderColor: 'rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/streamx" className="flex items-center gap-2.5" data-testid="livex-stories-back-livex">
          <img src={LOGO_URL} alt="Out-Play" className="h-9 w-9 rounded-xl object-cover" style={{ boxShadow: '0 0 0 2px rgba(255,59,48,0.30)' }} />
          <div className="flex items-center gap-2">
            <BrandText className="text-base text-white" />
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-white rounded"
              style={{ background: 'linear-gradient(135deg,#FF3B30,#990000)' }}>
              StreamX · Stories
            </span>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/streamx/live" data-testid="livex-stories-watch-live"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-white/80 hover:text-white transition-colors">
            <LiveDot size={2} /> Watch Live
          </Link>
          <Link to="/streamx" data-testid="livex-stories-livex-home"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-white/55 hover:text-white transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> StreamX home
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ counts }) {
  return (
    <section data-testid="livex-stories-hero" className="relative overflow-hidden border-b border-white/[0.08]">
      <div className="absolute inset-0 pointer-events-none" style={{
        background:
          'radial-gradient(60% 50% at 50% 0%, rgba(255,90,31,0.30), transparent 60%),' +
          'radial-gradient(40% 50% at 90% 35%, rgba(251,191,36,0.18), transparent 55%),' +
          'radial-gradient(40% 50% at 10% 70%, rgba(168,85,247,0.14), transparent 60%),' +
          '#0A0A0A',
      }} />
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }} />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-10 sm:pb-14 text-center">
        <Anim>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-black tracking-[0.18em] uppercase text-white"
            style={{ background: 'linear-gradient(135deg, rgba(220,38,38,0.18), rgba(249,115,22,0.12))', border: '1.5px solid rgba(255,90,31,0.45)' }}>
            <Trophy className="h-3.5 w-3.5 text-amber-300" />
            <span>The week in academy sports</span>
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.04] text-white">
            This week
            <br />
            <span style={{ background: 'linear-gradient(120deg, #FF4D4D 0%, #f97316 50%, #fbbf24 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              on StreamX.
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed font-medium text-[#cbd5e1]">
            The most-watched academy moments across the StreamX network. Editorial picks of the best matches,
            highlights, and rising academies — curated weekly.
          </p>
          <div className="mt-7 grid grid-cols-3 gap-3 max-w-md mx-auto">
            <MiniStat label="Matches" value={counts.replays_this_week || 0} icon={Film} color="#3b82f6" />
            <MiniStat label="Views" value={fmtViews(counts.total_views || 0)} icon={Eye} color="#22c55e" />
            <MiniStat label="Shares" value={fmtViews(counts.total_shares || 0)} icon={Share2} color="#FF6B5C" />
          </div>
        </Anim>
      </div>
    </section>
  );
}

function MiniStat({ label, value, icon: Icon, color }) {
  return (
    <div className="p-3 rounded-xl border border-white/[0.08] bg-white/[0.02]" data-testid={`livex-stories-mini-${label.toLowerCase()}`}>
      <div className="flex items-center justify-center gap-1.5">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        <span className="text-xl sm:text-2xl font-black text-white tabular-nums">{value}</span>
      </div>
      <div className="text-[10px] font-black uppercase tracking-[0.12em] text-white/55 mt-1">{label}</div>
    </div>
  );
}

function StoryThumbnail({ story, large = false, testid }) {
  const sport = (story.sport || '').toLowerCase();
  const gradient = SPORT_GRADIENTS[sport] || SPORT_GRADIENTS.default;
  const emoji = SPORT_EMOJI[sport] || '🎬';
  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden ${large ? 'aspect-video' : 'aspect-[16/10]'} rounded-2xl border border-white/[0.08] group`}
      style={{ background: gradient }}
    >
      {/* Cinematic emoji backdrop */}
      <div className="absolute inset-0 grid place-items-center text-white/15 text-9xl sm:text-[10rem]" aria-hidden>{emoji}</div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%)' }} />

      {/* Top-left LIVE/REPLAY badge */}
      <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 bg-black/70 backdrop-blur text-white text-[10px] font-black uppercase tracking-[0.1em] rounded">
        <Film className="h-3 w-3" /> Replay
      </div>

      {/* Top-right view count */}
      <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 bg-black/70 backdrop-blur text-white text-[10px] font-black uppercase tracking-[0.1em] rounded tabular-nums">
        <Eye className="h-3 w-3" /> {fmtViews(story.view_count)}
      </div>

      {/* Center play button */}
      <div className="absolute inset-0 grid place-items-center">
        <div
          className={`${large ? 'h-20 w-20' : 'h-14 w-14'} rounded-full grid place-items-center transition-transform group-hover:scale-110`}
          style={{ background: 'rgba(255,255,255,0.92)', boxShadow: '0 16px 36px rgba(0,0,0,0.45)' }}
        >
          <Play className={`${large ? 'h-9 w-9' : 'h-7 w-7'} text-[#0A0A0A] ml-1`} fill="currentColor" />
        </div>
      </div>

      {/* Bottom strip */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
        <div className="text-[11px] font-black tracking-[0.1em] uppercase text-white/85 truncate">
          {story.academy_name}
        </div>
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-black/70 backdrop-blur text-white text-[10px] font-bold rounded tabular-nums">
          <Clock className="h-3 w-3" /> {fmtDuration(story.duration_seconds)}
        </div>
      </div>
    </div>
  );
}

function StoryOfTheWeek({ story }) {
  const href = `/streamx/replays/public/${story.public_slug}`;
  return (
    <section data-testid="livex-stories-story-of-week" className="py-14 sm:py-20 border-b border-white/[0.08]"
      style={{ background: 'linear-gradient(180deg, #0A0A0A 0%, #050505 100%)' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <Anim className="mb-7">
          <div className="text-[11px] font-black tracking-[0.3em] text-[#FF6B5C] mb-2 inline-flex items-center gap-2">
            <Flame className="h-4 w-4" /> STORY OF THE WEEK
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">The match everyone is watching.</h2>
        </Anim>
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
          <Anim>
            <Link to={href} data-testid="livex-stories-hero-replay-link">
              <StoryThumbnail story={story} large testid="livex-stories-hero-thumbnail" />
            </Link>
          </Anim>
          <Anim delay={0.1}>
            <div className="h-full flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 mb-3">
                {story.sport && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-white rounded"
                    style={{ background: SPORT_GRADIENTS[(story.sport || '').toLowerCase()] || SPORT_GRADIENTS.default }}>
                    {SPORT_EMOJI[(story.sport || '').toLowerCase()] || '🎬'} {story.sport}
                  </span>
                )}
                <span className="text-[11px] font-black uppercase tracking-[0.12em] text-white/55">
                  {fmtRelative(story.recorded_at)}
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-3 leading-[1.1]">
                {story.title}
              </h3>
              <p className="text-base text-[#cbd5e1] font-medium mb-4">
                Streamed live by <b className="text-white">{story.academy_name}</b>. {story.highlights_count > 0 ? `${story.highlights_count} chapter${story.highlights_count !== 1 ? 's' : ''} marked.` : ''}
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <Stat icon={Eye} value={fmtViews(story.view_count)} label="views" />
                <Stat icon={Share2} value={fmtViews(story.share_count)} label="shares" />
                <Stat icon={Sparkles} value={story.highlights_count || 0} label="chapters" />
                <Stat icon={Clock} value={fmtDuration(story.duration_seconds)} label="duration" />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Link to={href} data-testid="livex-stories-hero-cta-watch"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-white rounded-xl transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #FF4D4D, #FF7A18)', boxShadow: '0 12px 32px rgba(255,90,31,0.45)' }}>
                  <Play className="h-4 w-4" fill="currentColor" /> Watch the story
                </Link>
                {story.academy_slug && (
                  <Link to={`/streamx/academy/${story.academy_slug}/top-moments`} data-testid="livex-stories-hero-cta-academy"
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FF6B5C] hover:underline">
                    See more from {story.academy_name} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>
          </Anim>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/[0.03] border border-white/[0.08]">
      <Icon className="h-3.5 w-3.5 text-white/55" />
      <span className="text-sm font-black text-white tabular-nums">{value}</span>
      <span className="text-[10px] font-black uppercase tracking-[0.1em] text-white/55">{label}</span>
    </div>
  );
}

function TrendingRow({ stories }) {
  return (
    <section data-testid="livex-stories-trending" className="py-14 sm:py-20 border-b border-white/[0.08]"
      style={{ background: '#0A0A0A' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <Anim className="mb-7 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-black tracking-[0.3em] text-[#FF6B5C] mb-2 inline-flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> TRENDING THIS WEEK
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Replays gaining traction.</h2>
          </div>
          <Link to="/streamx/live" data-testid="livex-stories-trending-watch-live"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FF6B5C] hover:underline">
            <LiveDot size={2} /> Watch what's live right now <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Anim>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {stories.map((s, i) => (
            <Anim key={s.id} delay={i * 0.06}>
              <TrendingCard story={s} index={i} />
            </Anim>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrendingCard({ story, index }) {
  const href = `/streamx/replays/public/${story.public_slug}`;
  return (
    <Link to={href} data-testid={`livex-stories-trending-card-${index}`} className="block group">
      <StoryThumbnail story={story} testid={`livex-stories-trending-thumb-${index}`} />
      <div className="mt-3 px-1">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-black uppercase tracking-[0.12em] text-white/55">
            #{index + 2} trending
          </span>
          <span className="text-[10px] font-black uppercase tracking-[0.12em] text-white/55">·</span>
          <span className="text-[10px] font-black uppercase tracking-[0.12em] text-white/55">
            {fmtRelative(story.recorded_at)}
          </span>
        </div>
        <h3 className="text-base font-black text-white group-hover:text-[#FF6B5C] transition-colors tracking-tight leading-snug line-clamp-2">
          {story.title}
        </h3>
        <div className="mt-1 text-[12px] font-bold text-white/55 truncate">{story.academy_name}</div>
      </div>
    </Link>
  );
}

function MostSharedCallout({ story }) {
  const href = `/streamx/replays/public/${story.public_slug}`;
  return (
    <section data-testid="livex-stories-most-shared" className="py-14 sm:py-20 border-b border-white/[0.08]"
      style={{ background: 'linear-gradient(180deg, #0A0A0A 0%, #14080A 100%)' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <Anim>
          <div className="grid md:grid-cols-[1.1fr_1fr] gap-6 items-center p-6 sm:p-8 rounded-3xl border-2 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(236,72,153,0.10), rgba(168,85,247,0.06))',
              borderColor: 'rgba(236,72,153,0.40)',
            }}>
            <div className="absolute inset-0 pointer-events-none opacity-40" style={{
              background: 'radial-gradient(60% 60% at 100% 0%, rgba(236,72,153,0.30), transparent 65%)',
            }} />
            <div className="relative">
              <Link to={href} data-testid="livex-stories-most-shared-link">
                <StoryThumbnail story={story} testid="livex-stories-most-shared-thumb" />
              </Link>
            </div>
            <div className="relative">
              <div className="text-[11px] font-black tracking-[0.3em] text-pink-300 mb-2 inline-flex items-center gap-2">
                <Share2 className="h-3.5 w-3.5" /> MOST SHARED MOMENT
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight mb-3">
                {story.title}
              </h3>
              <p className="text-sm text-white/75 mb-4">
                Shared <b className="text-pink-200 tabular-nums">{fmtViews(story.share_count)}</b> times by parents,
                friends, and coaches at <b className="text-white">{story.academy_name}</b>.
                This is the moment that traveled this week.
              </p>
              <Link to={href} data-testid="livex-stories-most-shared-cta"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold text-white rounded-lg border-2 border-pink-500/55 transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #ec4899, #a855f7)' }}>
                <Play className="h-3.5 w-3.5" fill="currentColor" /> See what everyone shared
              </Link>
            </div>
          </div>
        </Anim>
      </div>
    </section>
  );
}

function RisingAcademy({ rising }) {
  const academyHref = rising.academy_slug
    ? `/streamx/academy/${rising.academy_slug}/top-moments`
    : null;
  const sample = rising.sample_replay;
  const sampleHref = sample ? `/streamx/replays/public/${sample.public_slug}` : null;
  return (
    <section data-testid="livex-stories-rising" className="py-14 sm:py-20 border-b border-white/[0.08]"
      style={{ background: '#0A0A0A' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <Anim>
          <div className="text-[11px] font-black tracking-[0.3em] text-[#FF6B5C] mb-2 inline-flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> RISING ACADEMY
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-7">
            The academy compounding the fastest.
          </h2>
        </Anim>

        <Anim>
          <div className="grid md:grid-cols-[1fr_1.1fr] gap-5 items-stretch">
            <div
              className="relative p-7 rounded-2xl border-2 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #0A0A0A, #14080A)',
                borderColor: '#FF6B5C',
              }}
            >
              <div className="absolute inset-0 pointer-events-none opacity-40" style={{
                background: 'radial-gradient(60% 80% at 0% 100%, rgba(255,90,31,0.30), transparent 65%)',
              }} />
              <div className="relative">
                <div className="h-14 w-14 rounded-2xl grid place-items-center mb-4" style={{ background: 'linear-gradient(135deg,#FF3B30,#990000)', boxShadow: '0 12px 32px rgba(255,90,31,0.40)' }}>
                  <Trophy className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">{rising.academy_name}</h3>
                <div className="mt-1 text-[12px] font-black uppercase tracking-[0.12em] text-[#FF6B5C]">
                  {rising.replays_count} match{rising.replays_count !== 1 ? 'es' : ''} streamed this week
                </div>
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.08]">
                    <div className="text-[10px] font-black uppercase tracking-[0.12em] text-white/55">Views</div>
                    <div className="text-xl font-black text-white tabular-nums">{fmtViews(rising.view_total)}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.08]">
                    <div className="text-[10px] font-black uppercase tracking-[0.12em] text-white/55">Shares</div>
                    <div className="text-xl font-black text-white tabular-nums">{fmtViews(rising.share_total)}</div>
                  </div>
                </div>
                {academyHref && (
                  <Link to={academyHref} data-testid="livex-stories-rising-academy-link"
                    className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-white rounded-lg border-2 border-[#FF6B5C] transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)' }}>
                    Visit academy <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>
            {sample && sampleHref && (
              <Link to={sampleHref} data-testid="livex-stories-rising-sample-link" className="block group">
                <div className="h-full p-5 bg-white/[0.02] border border-white/[0.08] hover:border-[#FF6B5C] rounded-2xl transition-all">
                  <div className="text-[10px] font-black uppercase tracking-[0.12em] text-white/55 mb-3">Latest from {rising.academy_name}</div>
                  <StoryThumbnail story={sample} testid="livex-stories-rising-sample-thumb" />
                  <h4 className="mt-3 text-base font-black text-white group-hover:text-[#FF6B5C] transition-colors tracking-tight leading-snug line-clamp-2">
                    {sample.title}
                  </h4>
                  <div className="mt-1 text-[11px] font-bold text-white/55">
                    <Calendar className="inline h-3 w-3 mr-1" /> {fmtRelative(sample.recorded_at)}
                  </div>
                </div>
              </Link>
            )}
          </div>
        </Anim>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section data-testid="livex-stories-empty" className="py-20 border-b border-white/[0.08]" style={{ background: '#0A0A0A' }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
        <div className="h-16 w-16 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg,#1a0e10,#0A0A0A)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Film className="h-7 w-7 text-[#FF6B5C]" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">No stories yet this week.</h2>
        <p className="mt-3 text-base text-[#cbd5e1] font-medium">
          The StreamX Stories feed updates every week with the most-watched matches across the network.
          Be one of the first academies to broadcast — and you might be featured here next week.
        </p>
        <Link to="/streamx" data-testid="livex-stories-empty-cta"
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 text-sm font-bold text-white rounded-lg border-2 border-[#FF6B5C] transition-all hover:-translate-y-0.5"
          style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)' }}>
          <Radio className="h-4 w-4" /> Start broadcasting
        </Link>
      </div>
    </section>
  );
}

function BeFeaturedCTA() {
  return (
    <section data-testid="livex-stories-be-featured" className="py-16 sm:py-20 relative overflow-hidden border-b border-white/[0.08]"
      style={{ background: 'linear-gradient(135deg, #0a0a0f 0%, #1a0e10 50%, #0a0a0f 100%)' }}>
      <div className="absolute inset-0 opacity-30" style={{
        background: 'radial-gradient(circle at 30% 30%, rgba(255,90,31,0.5), transparent 50%), radial-gradient(circle at 70% 70%, rgba(220,38,38,0.4), transparent 50%)',
      }} />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <Anim>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-[0.18em] mb-5 text-white"
            style={{ background: 'rgba(255,90,31,0.18)', border: '1.5px solid rgba(255,90,31,0.55)' }}>
            <Sparkles className="h-3 w-3" /> Want to be featured?
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
            Stream your next match.
            <br />
            <span style={{ background: 'linear-gradient(120deg, #FF4D4D, #f97316, #fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              We'll do the rest.
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg font-medium max-w-xl mx-auto text-[#cbd5e1]">
            Every Monday, StreamX Stories features the academies generating the most engagement.
            Free pilot. No card. 60 seconds to go live.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-md mx-auto">
            <Link to="/streamx"
              data-testid="livex-stories-be-featured-explore"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-bold text-white rounded-xl transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #FF4D4D, #FF7A18)', boxShadow: '0 12px 32px rgba(255,90,31,0.55)' }}>
              <Radio className="h-5 w-5" /> Explore StreamX
            </Link>
            <Link to="/streamx/pricing"
              data-testid="livex-stories-be-featured-pricing"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-bold text-white bg-white/8 border-2 border-white/20 backdrop-blur hover:bg-white/15 rounded-xl transition-all">
              See Pricing
            </Link>
          </div>
        </Anim>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A0A0A]" data-testid="livex-stories-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[12px] font-bold text-white/55">
          <Trophy className="h-3.5 w-3.5 text-[#FF6B5C]" />
          StreamX Stories — Curated weekly · Open to every academy on StreamX
        </div>
        <div className="flex items-center gap-4 text-[12px] font-bold">
          <Link to="/streamx" className="text-white/55 hover:text-[#FF6B5C] transition-colors">StreamX home</Link>
          <Link to="/streamx/live" className="text-white/55 hover:text-[#FF6B5C] transition-colors">Live now</Link>
          <Link to="/streamx/pricing" className="text-white/55 hover:text-[#FF6B5C] transition-colors">Pricing</Link>
        </div>
      </div>
    </footer>
  );
}
